import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, Calculator, Zap } from 'lucide-react';
import type { KalkulationsZeile, PositionsKalkulation } from '@shared/schema';
import {
  berechneKalkulation,
  berechneMengenBezugWechselVorschau,
  einheitspreisAusKalkulation,
  gesamtAusEinheitspreis,
  hatKalkulation,
  istKalkulationGueltigFuerEpAbleitung,
  neueZeilenId,
  warntEinheitNachtragImTiefenModal,
  wendeZeilenUpdateMitHerkunftsSchutzAn,
  zeilenSumme,
} from '@shared/kalkulation';
import { useOfferStore } from '@/lib/store';

interface PositionKalkulationModalProps {
  positionId: number | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ART_LABELS: Record<KalkulationsZeile['art'], string> = {
  lohn: 'Arbeitsstunden / Lohn',
  material: 'Material',
  sonstiges: 'Sonstiges',
};

function parseInput(value: string): number | undefined {
  if (value.trim() === '') return undefined;
  const n = Number(value.replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function formatEuro(value: number): string {
  return value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Tiefenkalkulation: Kalkulation einer LV-Position, unterteilt nach
 * Arbeitsstunden/Lohn, Material (EK + Aufschlag) und Sonstiges.
 * Die Summe ergibt das Positionsgesamt; der Einheitspreis wird daraus abgeleitet.
 */
export function PositionKalkulationModal({ positionId, open, onOpenChange }: PositionKalkulationModalProps) {
  const positions = useOfferStore((s) => s.positions);
  const quoteItems = useOfferStore((s) => s.quoteItems);
  const defaults = useOfferStore((s) => s.kalkulationsDefaults);
  const updatePositionKalkulation = useOfferStore((s) => s.updatePositionKalkulation);

  const position = positionId !== null ? positions.find((p) => p.id === positionId) : undefined;
  const quoteItem = positionId !== null ? quoteItems.find((q) => q.id === positionId) : undefined;
  const positionsMenge = Number(quoteItem?.menge) > 0 ? Number(quoteItem?.menge) : 1;

  const [draft, setDraft] = useState<PositionsKalkulation>({ zeilen: [] });
  // Zeilen-IDs, die beim Öffnen des Modals BEREITS gespeichert waren — nur für danach
  // NEU hinzugefügte Lohn-/Sonstiges-Zeilen warnt die "je Einheit"-Box (Pendant zur
  // Misch-Warnung im Blitz-Pfad, siehe warntEinheitNachtragImTiefenModal).
  const [bestandsZeilenIds, setBestandsZeilenIds] = useState<string[]>([]);

  useEffect(() => {
    if (open && position) {
      const existing = position.kalkulation;
      setDraft(
        existing
          // Bestehende Kalkulation: mengenBezug bleibt wie gespeichert (fehlt das Feld,
          // bedeutet das 'gesamt' — bisheriges Verhalten, damit sich EPs nicht still ändern).
          ? { ...existing, zeilen: existing.zeilen.map((z) => ({ ...z })) }
          // Neue Kalkulation: Default 'einheit' (Kalkulation gilt je Einheit).
          : { zeilen: [], mengenBezug: 'einheit' },
      );
      setBestandsZeilenIds(existing ? existing.zeilen.map((z) => z.id) : []);
    }
  }, [open, position]);

  const summen = useMemo(() => berechneKalkulation(draft, defaults), [draft, defaults]);
  const einheitspreis = useMemo(
    () => einheitspreisAusKalkulation(draft, positionsMenge, defaults),
    [draft, positionsMenge, defaults],
  );
  const gesamtBetrag = useMemo(
    () => gesamtAusEinheitspreis(einheitspreis, positionsMenge),
    [einheitspreis, positionsMenge],
  );
  // Misch-Warnung (Pendant zum Blitz-Pfad): neu nachgetragene Lohn-/Sonstiges-Zeilen in
  // einer 'einheit'-Kalkulation bei Menge ≠ 1 — nicht blockierend, mit konkreten Zahlen.
  const einheitNachtragWarnungen = useMemo(
    () => warntEinheitNachtragImTiefenModal(draft, bestandsZeilenIds, positionsMenge, defaults),
    [draft, bestandsZeilenIds, positionsMenge, defaults],
  );
  // Für die Anzeige des Umschalters: fehlendes Feld gilt als 'gesamt' (siehe Kommentar oben).
  const mengenBezug: 'einheit' | 'gesamt' = draft.mengenBezug ?? 'gesamt';
  const setMengenBezug = (value: 'einheit' | 'gesamt') => {
    setDraft((d) => ({ ...d, mengenBezug: value }));
  };

  if (!position) return null;

  const einheitLabel = position.einheit || 'Stk';
  const bestehenderEp = Number(position.preis) || 0;
  // Schutz: Eine leere/nullwertige Kalkulation darf einen vorhandenen EP nicht auf 0,00 € überschreiben.
  const schuetztBestehendenEp = einheitspreis === 0 && bestehenderEp > 0;
  // P2-5 (Fix, Regression aus P1-B): hatKalkulation() lässt eine reine negative Zeile (z.B. ein
  // Rabatt ohne zugehörige Kostenzeile) als "befüllt" gelten — der Save-Button darf sich dadurch
  // NICHT freischalten, sonst leitet handleSave einen negativen (oder nicht-positiven)
  // Einzelpreis ab und speichert ihn. istKalkulationGueltigFuerEpAbleitung prüft stattdessen die
  // tatsächliche Netto-Summe (muss > 0 sein).
  const kalkulationUngueltigFuerEp =
    draft.zeilen.length > 0 && !istKalkulationGueltigFuerEpAbleitung(draft, defaults);

  // Review-Auflage A (GPT-5.6/Grok, 2026-08): der Bezug-Umschalter deutet JEDE Zeile neu —
  // weicht der Draft-Bezug vom GESPEICHERTEN Bezug ab und ändert das den Preis tatsächlich,
  // wird die Wirkung isoliert vorher/nachher angezeigt (auf dem aktuellen Draft gerechnet,
  // damit auch parallel editierte Zeilen berücksichtigt sind). Persistiert wird ohnehin erst
  // mit dem expliziten "Preis übernehmen"-Klick, dessen Button den neuen EP bereits nennt.
  const gespeicherterBezug: 'einheit' | 'gesamt' | null = position?.kalkulation
    ? position.kalkulation.mengenBezug === 'einheit'
      ? 'einheit'
      : 'gesamt'
    : null;
  const bezugWechselVorschau =
    gespeicherterBezug !== null && mengenBezug !== gespeicherterBezug
      ? berechneMengenBezugWechselVorschau(
          { ...draft, mengenBezug: gespeicherterBezug },
          mengenBezug,
          positionsMenge,
          defaults,
        )
      : null;
  const zeigeBezugWechselHinweis = bezugWechselVorschau !== null && bezugWechselVorschau.aendertPreis;

  const zeilenFuer = (art: KalkulationsZeile['art']) => draft.zeilen.filter((z) => z.art === art);

  const addZeile = (art: KalkulationsZeile['art']) => {
    setDraft((d) => ({
      ...d,
      zeilen: [...d.zeilen, { id: neueZeilenId(), art }],
    }));
  };

  const updateZeile = (id: string, updates: Partial<KalkulationsZeile>) => {
    setDraft((d) => ({
      ...d,
      // Snapshot-Pflicht Live-Preis (Muster mengeHerkunft): ein manuell geänderter Betrag
      // (einzelpreis) löscht den preisHerkunft-Vermerk der Zeile — siehe
      // shared/kalkulation.ts::wendeZeilenUpdateMitHerkunftsSchutzAn.
      zeilen: d.zeilen.map((z) => (z.id === id ? wendeZeilenUpdateMitHerkunftsSchutzAn(z, updates) : z)),
    }));
  };

  const removeZeile = (id: string) => {
    setDraft((d) => ({ ...d, zeilen: d.zeilen.filter((z) => z.id !== id) }));
  };

  const handleSave = () => {
    // updatePositionKalkulation liefert false, wenn die Kalkulation abgelehnt wird
    // (nicht-positive Netto-Summe). Der Save-Button ist dafuer zwar per
    // kalkulationUngueltigFuerEp disabled, aber ein schmaler Race (Position zwischen
    // Render und Klick entfernt) bleibt: dann NICHT schliessen (kein falsches "gespeichert").
    if (!updatePositionKalkulation(position.id, draft.zeilen.length > 0 ? draft : null)) return;
    onOpenChange(false);
  };

  const handleRemoveAll = () => {
    updatePositionKalkulation(position.id, null);
    onOpenChange(false);
  };

  const renderSection = (art: KalkulationsZeile['art']) => {
    const zeilen = zeilenFuer(art);
    // Volle Klassennamen als Literale (nicht dynamisch zusammensetzen), damit Tailwind
    // die arbitrary-value-Klasse beim Scannen des Quellcodes zuverlässig erkennt.
    const gridColsClass =
      art === 'lohn'
        ? 'grid grid-cols-[1fr_128px_110px_100px_90px_28px] items-center gap-1.5'
        : 'grid grid-cols-[1fr_90px_110px_100px_90px_28px] items-center gap-1.5';
    return (
      <div key={art} className="rounded-md border border-gray-200 p-3">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-800">{ART_LABELS[art]}</h4>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-blue-600 hover:bg-blue-50"
            onClick={() => addZeile(art)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" /> Zeile
          </Button>
        </div>

        {zeilen.length === 0 ? (
          <p className="text-xs text-gray-400">Keine Zeilen.</p>
        ) : (
          <div className="space-y-1.5">
            <div className={`${gridColsClass} text-[11px] uppercase tracking-wide text-gray-400`}>
              <span>Beschreibung</span>
              <span className="text-right">{art === 'lohn' ? 'Zeit' : 'Menge'}</span>
              <span className="text-right">{art === 'lohn' ? '€/h' : art === 'material' ? 'EK €' : 'Betrag €'}</span>
              <span className="text-right">{art === 'material' ? 'Aufschlag %' : ''}</span>
              <span className="text-right">Summe</span>
              <span />
            </div>
            {zeilen.map((zeile) => (
              <div key={zeile.id} className={gridColsClass}>
                <Input
                  value={zeile.beschreibung ?? ''}
                  onChange={(e) => updateZeile(zeile.id, { beschreibung: e.target.value })}
                  placeholder={art === 'lohn' ? 'z.B. Montage' : art === 'material' ? 'z.B. Kabel NYM 3x1,5' : 'z.B. Gerüst'}
                  className="h-8 text-sm"
                />
                {art === 'lohn' ? (
                  <div className="flex items-center gap-1">
                    <Input
                      type="text"
                      inputMode="decimal"
                      defaultValue={zeile.menge !== undefined ? String(zeile.menge).replace('.', ',') : ''}
                      key={`${zeile.id}-menge-${open}`}
                      onChange={(e) => updateZeile(zeile.id, { menge: parseInput(e.target.value) })}
                      placeholder="0"
                      className="h-8 min-w-0 flex-1 text-right text-sm"
                    />
                    <select
                      value={zeile.zeitEinheit ?? 'h'}
                      onChange={(e) => updateZeile(zeile.id, { zeitEinheit: e.target.value as 'h' | 'min' })}
                      className="h-8 w-14 shrink-0 rounded border border-gray-200 bg-white text-xs"
                      aria-label="Zeiteinheit"
                    >
                      <option value="h">Std</option>
                      <option value="min">Min</option>
                    </select>
                  </div>
                ) : (
                  <Input
                    type="text"
                    inputMode="decimal"
                    defaultValue={zeile.menge !== undefined ? String(zeile.menge).replace('.', ',') : ''}
                    key={`${zeile.id}-menge-${open}`}
                    onChange={(e) => updateZeile(zeile.id, { menge: parseInput(e.target.value) })}
                    placeholder="1"
                    className="h-8 text-right text-sm"
                  />
                )}
                <Input
                  type="text"
                  inputMode="decimal"
                  defaultValue={zeile.einzelpreis !== undefined ? String(zeile.einzelpreis).replace('.', ',') : ''}
                  key={`${zeile.id}-preis-${open}`}
                  onChange={(e) => updateZeile(zeile.id, { einzelpreis: parseInput(e.target.value) })}
                  placeholder={art === 'lohn' ? String(defaults.stundensatz).replace('.', ',') : '0,00'}
                  className="h-8 text-right text-sm"
                />
                {art === 'material' ? (
                  <Input
                    type="text"
                    inputMode="decimal"
                    defaultValue={zeile.aufschlagProzent !== undefined ? String(zeile.aufschlagProzent).replace('.', ',') : ''}
                    key={`${zeile.id}-aufschlag-${open}`}
                    onChange={(e) => updateZeile(zeile.id, { aufschlagProzent: parseInput(e.target.value) })}
                    placeholder={String(defaults.materialAufschlagProzent).replace('.', ',')}
                    className="h-8 text-right text-sm"
                  />
                ) : (
                  <span />
                )}
                <span className="text-right text-sm font-medium tabular-nums">
                  {formatEuro(zeilenSumme(zeile, draft, defaults))}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-gray-400 hover:bg-red-50 hover:text-red-600"
                  onClick={() => removeZeile(zeile.id)}
                  aria-label="Zeile entfernen"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
                {/* Snapshot-Herkunft Live-Preis (Badge analog IFC-mengeHerkunft in
                    simple-table.tsx): zeigt, WOHER der eingefrorene Betrag stammt.
                    Verschwindet automatisch, sobald der Betrag manuell geändert wird
                    (wendeZeilenUpdateMitHerkunftsSchutzAn löscht den Vermerk). */}
                {zeile.preisHerkunft && (
                  <p
                    className="col-span-6 -mt-0.5 flex items-center gap-1 text-[11px] text-sky-700"
                    title={`Live-Preis der Händler-API · Lieferant ${zeile.preisHerkunft.lieferant || '—'} · Quote ${zeile.preisHerkunft.quoteId} · abgerufen ${new Date(zeile.preisHerkunft.fetchedAt).toLocaleString('de-DE')} · Preiseinheit ${zeile.preisHerkunft.priceUnit || '—'} (Divisor ${zeile.preisHerkunft.faktor})`}
                  >
                    <Zap className="h-3 w-3 flex-shrink-0" />
                    Live-Preis vom {new Date(zeile.preisHerkunft.fetchedAt).toLocaleDateString('de-DE')} — bei manueller Änderung des Betrags wird der Vermerk entfernt
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-blue-600" />
            Tiefenkalkulation
            {position.positionNumber ? ` — Pos. ${position.positionNumber}` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
          <div className="rounded-md bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-800">{position.name || position.beschreibung || 'Position'}</p>
            <p className="text-xs text-gray-500">
              Positionsmenge: {String(positionsMenge).replace('.', ',')} {einheitLabel}
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-700">
              <span className="font-medium text-gray-500">Kalkulation gilt:</span>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="mengenBezug"
                  checked={mengenBezug === 'einheit'}
                  onChange={() => setMengenBezug('einheit')}
                />
                je {einheitLabel}
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="mengenBezug"
                  checked={mengenBezug === 'gesamt'}
                  onChange={() => setMengenBezug('gesamt')}
                />
                für die Gesamtmenge ({String(positionsMenge).replace('.', ',')} {einheitLabel})
              </label>
            </div>
          </div>

          {renderSection('lohn')}
          {renderSection('material')}
          {renderSection('sonstiges')}

          {einheitNachtragWarnungen.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">
                Achtung: neue Zeilen werden JE {einheitLabel} gerechnet (Positionsmenge:{' '}
                {String(positionsMenge).replace('.', ',')} {einheitLabel})
              </p>
              {einheitNachtragWarnungen.map((w) => (
                <p key={w.zeilenId} className="mt-1 tabular-nums">
                  „{w.beschreibung}": {formatEuro(w.betragJeEinheit)} je {einheitLabel} — bei{' '}
                  {String(positionsMenge).replace('.', ',')} {einheitLabel} ergibt das{' '}
                  {formatEuro(w.betragJeEinheit)} × {String(positionsMenge).replace('.', ',')} ={' '}
                  {formatEuro(w.gesamtBetrag)} im Gesamtbetrag.
                </p>
              ))}
              <p className="mt-1">
                Gilt ein Betrag für die GESAMTE Position, oben auf „für die Gesamtmenge ({String(positionsMenge).replace('.', ',')}{' '}
                {einheitLabel})" umstellen — es wird nichts automatisch umgerechnet.
              </p>
            </div>
          )}

          <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-4">
              <div>
                <p className="text-xs text-gray-500">Lohn ({String(summen.lohnStunden).replace('.', ',')} h)</p>
                <p className="font-medium tabular-nums">{formatEuro(summen.lohnSumme)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">
                  Material (EK {formatEuro(summen.materialEkSumme)} + {formatEuro(summen.materialAufschlagSumme)})
                </p>
                <p className="font-medium tabular-nums">{formatEuro(summen.materialSumme)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Sonstiges</p>
                <p className="font-medium tabular-nums">{formatEuro(summen.sonstigesSumme)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Kalk-Summe ({mengenBezug === 'einheit' ? `je ${einheitLabel}` : 'gesamt'})</p>
                <p className="font-medium tabular-nums">{formatEuro(summen.gesamtNetto)}</p>
              </div>
            </div>
            <p className="mt-2 border-t border-blue-200 pt-2 font-semibold tabular-nums text-blue-800">
              EP: {formatEuro(einheitspreis)}/{einheitLabel}
              {' · '}Gesamt (Menge×EP): {formatEuro(gesamtBetrag)}
            </p>
          </div>

          {zeigeBezugWechselHinweis && bezugWechselVorschau && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="font-semibold">
                Bezug umgestellt: {mengenBezug === 'einheit' ? `je ${einheitLabel}` : 'für die Gesamtmenge'} (gespeichert
                war: {gespeicherterBezug === 'einheit' ? `je ${einheitLabel}` : 'für die Gesamtmenge'})
              </p>
              <p className="mt-1 tabular-nums">
                EP: {formatEuro(bezugWechselVorschau.epVorher)} → {formatEuro(bezugWechselVorschau.epNachher)}
                {' · '}Gesamt: {formatEuro(bezugWechselVorschau.gesamtVorher)} →{' '}
                {formatEuro(bezugWechselVorschau.gesamtNachher)}
              </p>
              <p className="mt-1">
                Die Zeilenwerte bleiben unverändert und werden nur neu gedeutet — gespeichert wird erst mit „Preis
                übernehmen".
              </p>
            </div>
          )}
        </div>

        {schuetztBestehendenEp && (
          <p className="flex-shrink-0 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            Die Kalkulation ergibt 0,00 €. Der bestehende Einheitspreis {formatEuro(bestehenderEp)}/{einheitLabel} bleibt
            geschützt — bitte zuerst Stunden oder Beträge erfassen.
          </p>
        )}
        {kalkulationUngueltigFuerEp && !schuetztBestehendenEp && (
          <p className="flex-shrink-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
            Diese Kalkulation ergibt einen nicht-positiven Betrag (0,00 € oder negativ) — daraus kann kein gültiger
            Einzelpreis abgeleitet werden. Bitte prüfen (z. B. eine Rabatt-Zeile ohne zugehörige Kostenzeile).
          </p>
        )}
        <DialogFooter className="flex-shrink-0 gap-2">
          {hatKalkulation(position.kalkulation) && (
            <Button
              type="button"
              variant="ghost"
              onClick={handleRemoveAll}
              className="mr-auto text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              Kalkulation entfernen
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={kalkulationUngueltigFuerEp || schuetztBestehendenEp}
            title={
              kalkulationUngueltigFuerEp
                ? 'Deaktiviert: Diese Kalkulation ergibt einen nicht-positiven Betrag (0,00 € oder negativ) — kein gültiger Einzelpreis ableitbar.'
                : schuetztBestehendenEp
                  ? `Deaktiviert: Der bestehende Einheitspreis ${formatEuro(bestehenderEp)}/${einheitLabel} würde sonst mit 0,00 € überschrieben.`
                  : undefined
            }
          >
            Preis übernehmen ({formatEuro(einheitspreis)}/{einheitLabel})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
